import { isPlanId, type PlanId, type Regimen } from "@/data/meal-plan";
import type { DayOfWeek, WeekPlan } from "@/types/meal-plan-state";
import type { NoteHistory } from "./note-suggestions";
import type { DayStatusInfo, WeekStatus } from "./plan-status";
import { sanitizeWeekPlan } from "./week-plan-validation";
import type { StoreId } from "./stores";

export type RegimenPresets = Record<DayOfWeek, Regimen["id"] | null>;

export async function fetchPresets(): Promise<RegimenPresets> {
  const res = await fetch("/api/presets");
  if (!res.ok) {
    return { mon: null, tue: null, wed: null, thu: null, fri: null, sat: null, sun: null };
  }
  const data = await res.json() as { presets: RegimenPresets };
  return data.presets ?? { mon: null, tue: null, wed: null, thu: null, fri: null, sat: null, sun: null };
}

export async function savePreset(
  day: DayOfWeek,
  regimenId: Regimen["id"],
): Promise<void> {
  await fetch(`/api/presets/${day}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ regimenId }),
  });
}

// ── Week scope: my weeks, or an accepted friend's ──

/** Whose weeks a request reads or writes. Friend endpoints mirror mine under `/api/friends/:id`. */
export type WeekScope = { kind: "self" } | { kind: "friend"; friendId: number };

export const SELF_SCOPE: WeekScope = { kind: "self" };

function scopePrefix(scope: WeekScope): string {
  return scope.kind === "self" ? "/api" : `/api/friends/${scope.friendId}`;
}

export interface WeekSummary {
  weekId: string;
  updatedAt: string;
  status: WeekStatus;
  dayStatuses: Record<DayOfWeek, DayStatusInfo>;
  plannedDayCount: number;
}

export async function fetchWeekList(scope: WeekScope): Promise<WeekSummary[]> {
  const res = await fetch(`${scopePrefix(scope)}/weeks`);
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { weeks: WeekSummary[] };
  return data.weeks;
}

// ── Versioned week sync ──

/** "edit": a person changed the week. "system": creation, migration or plan adoption. */
export type SaveKind = "edit" | "system";

export interface WeekEditor {
  id: number;
  username: string;
}

/** The last edit of my week by someone else (a friend planning it); `at` is ISO UTC. */
export interface PlannerEdit {
  by: WeekEditor;
  at: string;
}

/**
 * The server's copy of a week. `version` is 0 when the week doesn't exist yet.
 * `editedBy`/`editedAt` (ISO UTC) is its last human edit, owner or planner;
 * `plannerEdit` its last edit by someone other than the owner.
 */
export interface WeekSnapshot {
  plan: WeekPlan | null;
  version: number;
  editedBy: WeekEditor | null;
  editedAt: string | null;
  plannerEdit: PlannerEdit | null;
}

export type SaveWeekResult =
  | ({ status: "saved"; version: number } & Pick<WeekSnapshot, "editedBy" | "editedAt" | "plannerEdit">)
  /** Someone saved first: `current` is the server's copy to rebase onto. */
  | { status: "conflict"; current: WeekSnapshot }
  | { status: "forbidden" };

/** A hung request must end in an error the UI can retry, not an endless spinner. */
const WEEK_REQUEST_TIMEOUT_MS = 15_000;

/** The one place week URLs are built. */
function weekUrl(scope: WeekScope, weekId: string): string {
  return `${scopePrefix(scope)}/weeks/${encodeURIComponent(weekId)}`;
}

/** A snapshot from the server; days of its plan that fail validation come back empty (`sanitizeWeekPlan`). */
function parseSnapshot(data: unknown): WeekSnapshot {
  const raw = (data ?? {}) as Partial<WeekSnapshot>;
  if (typeof raw.version !== "number") throw new Error("Respuesta inválida del servidor");
  return {
    plan: raw.plan == null ? null : sanitizeWeekPlan(raw.plan),
    version: raw.version,
    editedBy: raw.editedBy ?? null,
    editedAt: raw.editedAt ?? null,
    plannerEdit: raw.plannerEdit ?? null,
  };
}

/** Throws on any non-2xx response or a body without a version. */
export async function fetchWeekSnapshot(scope: WeekScope, weekId: string): Promise<WeekSnapshot> {
  const res = await fetch(weekUrl(scope, weekId), { signal: AbortSignal.timeout(WEEK_REQUEST_TIMEOUT_MS) });
  if (!res.ok) throw new Error(await readApiError(res));
  return parseSnapshot(await res.json());
}

/**
 * Save `plan` if the server still has `baseVersion` (0: "the week doesn't
 * exist yet"). A 409 comes back as a conflict carrying the server's copy and a
 * 403 (a friend's week after the friendship ended) as forbidden; network
 * failures and other statuses throw.
 */
export async function saveWeekVersioned(
  scope: WeekScope,
  weekId: string,
  plan: WeekPlan,
  baseVersion: number,
  kind: SaveKind,
): Promise<SaveWeekResult> {
  const res = await fetch(weekUrl(scope, weekId), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan, baseVersion, kind }),
    // On a timeout the save may still have landed; the retry's 409 reconciles it.
    signal: AbortSignal.timeout(WEEK_REQUEST_TIMEOUT_MS),
  });
  if (res.status === 409) return { status: "conflict", current: parseSnapshot(await res.json()) };
  if (res.status === 403) return { status: "forbidden" };
  if (!res.ok) throw new Error(await readApiError(res));
  const saved = parseSnapshot(await res.json());
  return {
    status: "saved",
    version: saved.version,
    editedBy: saved.editedBy,
    editedAt: saved.editedAt,
    plannerEdit: saved.plannerEdit,
  };
}

export async function deleteWeekPlan(weekId: string): Promise<void> {
  await fetch(`/api/weeks/${weekId}`, { method: "DELETE" });
}

/** Every meal note across the scope's weeks, with the days it was picked (Comidas autocomplete). */
export async function fetchMealNoteHistory(scope: WeekScope): Promise<NoteHistory[]> {
  const res = await fetch(`${scopePrefix(scope)}/meal-notes`);
  if (!res.ok) return [];
  const data = (await res.json()) as { history?: NoteHistory[] };
  return data.history ?? [];
}

/** A week owner's plan and weekday presets (remapped to that plan by the server). */
export interface OwnerContext {
  planId: PlanId;
  presets: RegimenPresets;
}

/** An accepted friend's plan and presets. Throws on any error, a 403 included. */
export async function fetchFriendPlan(friendId: number): Promise<OwnerContext> {
  const res = await fetch(`/api/friends/${friendId}/plan`, { signal: AbortSignal.timeout(WEEK_REQUEST_TIMEOUT_MS) });
  if (!res.ok) throw new Error(await readApiError(res));
  const data = (await res.json()) as { planId?: unknown; presets?: RegimenPresets };
  if (!isPlanId(data.planId)) throw new Error("Plan desconocido");
  return { planId: data.planId, presets: data.presets ?? { mon: null, tue: null, wed: null, thu: null, fri: null, sat: null, sun: null } };
}

// ── Friends ──

export interface Friend {
  id: number;
  friend_id: number;
  username: string;
  plan_id: string;
  status: "pending" | "accepted";
  direction: "sent" | "received";
}

/** Throws on any error: a failed refresh must not read as "no friends". */
export async function fetchFriends(): Promise<Friend[]> {
  const res = await fetch("/api/friends");
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { friends: Friend[] };
  return data.friends;
}

export async function sendFriendRequest(username: string): Promise<void> {
  const res = await fetch("/api/friends", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });
  if (!res.ok) throw new Error(await readApiError(res));
}

export async function acceptFriendRequest(id: number): Promise<void> {
  const res = await fetch(`/api/friends/${id}/accept`, { method: "POST" });
  if (!res.ok) throw new Error(await readApiError(res));
}

/** Removes a friend, or rejects / cancels a pending request. */
export async function removeFriend(id: number): Promise<void> {
  const res = await fetch(`/api/friends/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(await readApiError(res));
}

// ── Auth ──

export interface AuthUser {
  id: number;
  username: string;
  planId: string;
  preferredStore: StoreId;
}

export async function fetchMe(): Promise<AuthUser | null> {
  const res = await fetch("/api/auth/me");
  const data = await res.json() as { user: AuthUser | null };
  return data.user;
}

export async function login(username: string, password: string): Promise<AuthUser> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const data = await res.json() as { error: string };
    throw new Error(data.error);
  }
  const data = await res.json() as { user: AuthUser };
  return data.user;
}

export async function signup(username: string, password: string, planId: string): Promise<AuthUser> {
  const res = await fetch("/api/auth/signup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password, planId }),
  });
  if (!res.ok) {
    const data = await res.json() as { error: string };
    throw new Error(data.error);
  }
  const data = await res.json() as { user: AuthUser };
  return data.user;
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST" });
}

// ── Plan ──

/** Switch the user's plan; the server remaps presets outside it and returns them. */
export async function savePlan(planId: PlanId): Promise<{ planId: PlanId; presets: RegimenPresets }> {
  const res = await fetch("/api/plan", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ planId }),
  });
  if (!res.ok) throw new Error(await readApiError(res));
  return await res.json() as { planId: PlanId; presets: RegimenPresets };
}

// ── Store preference ──

export async function updatePreferredStore(store: StoreId): Promise<StoreId> {
  const res = await fetch("/api/preferences", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ store }),
  });
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { store: StoreId };
  return data.store;
}

// ── Supermarkets (Wong, PlazaVea) ──

export interface StoreStatus {
  connected: boolean;
  email: string | null;
  authExpiresAt: string | null;
  updatedAt: string | null;
  configured: boolean;
}

export interface StoreProduct {
  productId: string;
  productName: string;
  brand: string;
  linkText: string;
  categories: string[];
  skuId: string;
  sellerId: string;
  sellerName: string;
  price: number;
  listPrice: number;
  availableQuantity: number;
  isAvailable: boolean;
  measurementUnit: string;
  unitMultiplier: number;
  imageUrl: string | null;
  packageAmount: number | null;
  packageUnit: "g" | "kg" | "ml" | "l" | "un" | null;
  raw: unknown;
}

export interface StoreMapping {
  ingredientName: string;
  productId: string;
  skuId: string;
  sellerId: string;
  productName: string;
  brand: string | null;
  imageUrl: string | null;
  measurementUnit: string;
  unitMultiplier: number;
  conversionMode: "measurable" | "grouped-units";
  packageAmount: number | null;
  packageUnit: string | null;
  unitsPerSku: number | null;
  updatedAt: string;
}

export interface StoreCartAddItem {
  ingredientName: string;
  totalGrams: number;
  totalUnits: number;
  requiredAmountLabel: string;
}

export interface StoreCartAddResult {
  orderFormId?: string;
  loggedIn?: boolean;
  value?: number;
  added: Array<{
    skuId: string;
    sellerId: string;
    quantity: number;
    ingredientName: string;
    productName: string;
    requiredAmountLabel: string;
    before: number;
    after: number;
    delta: number;
  }>;
  skipped: Array<{ ingredientName: string; reason: string }>;
  messages?: unknown[];
}

export interface StoreCartProduct {
  skuId: string;
  productId: string | null;
  name: string;
  quantity: number;
  seller: string | null;
  sellerName: string | null;
  price: number;
  listPrice: number;
  sellingPrice: number;
  imageUrl: string | null;
  detailUrl: string | null;
  availability: string | null;
  measurementUnit: string | null;
  unitMultiplier: number | null;
}

export interface StoreCart {
  orderFormId: string | null;
  loggedIn: boolean;
  value: number;
  itemCount: number;
  items: StoreCartProduct[];
  messages: unknown[];
  canEditData: boolean;
  userProfileId: string | null;
  hasSelectedAddress: boolean;
  selectedAddressCount: number;
  logisticsInfoCount: number;
}

// Fields every store's delivery location exposes; enough for the Compras
// strip and the sheet's "view" mode.
export interface StoreDeliveryLocation {
  configured: boolean;
  street: string | null;
  number: string | null;
  neighborhood: string | null;
  postalCode: string | null;
}

export interface PlazaVeaDeliveryLocation extends StoreDeliveryLocation {
  latitude: number | null;
  longitude: number | null;
  salesChannel: string | null;
  regionId: string | null;
  city: string | null;
  state: string | null;
  addressId: string | null;
}

export interface WongDeliveryLocation extends StoreDeliveryLocation {
  addressId: string | null;
  complement: string | null;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
}

// One of the shopper's saved addresses in their Wong account.
export interface WongSavedAddress {
  addressId: string;
  street: string;
  number: string;
  neighborhood: string;
  complement: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  latitude: number;
  longitude: number;
  selectedInWong: boolean;
}

export interface WongLocationState {
  location: WongDeliveryLocation;
  savedAddresses: WongSavedAddress[];
}

export interface PlazaVeaCoverageStore {
  name: string;
  address: string | null;
  salesChannel: string | null;
  warehouseId: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface PlazaVeaCoverageDistrict {
  name: string;
  displayName: string;
  ubigeo: string;
  provinceName: string | null;
  provinceUbigeo: string | null;
  departmentName: string | null;
  departmentUbigeo: string | null;
  salesChannel: string | null;
  hasExpress: boolean;
  expressSalesChannel: string | null;
  hasDarkStore: boolean;
  darkStoreSalesChannel: string | null;
  stores: PlazaVeaCoverageStore[];
}

export interface PlazaVeaCoverage {
  districts: PlazaVeaCoverageDistrict[];
}

export interface PlazaVeaReverseGeocodeResult {
  latitude: number;
  longitude: number;
  street: string | null;
  number: string | null;
  district: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  displayName: string | null;
  raw: unknown;
}

async function readApiError(res: Response): Promise<string> {
  try {
    const data = await res.json() as { error?: string };
    return data.error ?? `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

function storePath(store: StoreId, path: string): string {
  return `/api/stores/${store}${path}`;
}

function postJson(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export async function fetchStoreStatus(store: StoreId): Promise<StoreStatus> {
  const res = await fetch(storePath(store, "/status"));
  if (!res.ok) throw new Error(await readApiError(res));
  return await res.json() as StoreStatus;
}

export async function connectStore(store: StoreId, email: string, password: string): Promise<StoreStatus> {
  const res = await fetch(storePath(store, "/connect"), postJson({ email, password }));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { connected: boolean; email: string; authExpiresAt: string | null };
  return {
    connected: data.connected,
    email: data.email,
    authExpiresAt: data.authExpiresAt,
    updatedAt: null,
    configured: true,
  };
}

export async function disconnectStore(store: StoreId): Promise<void> {
  const res = await fetch(storePath(store, "/connect"), { method: "DELETE" });
  if (!res.ok) throw new Error(await readApiError(res));
}

export async function fetchPlazaVeaLocation(): Promise<PlazaVeaDeliveryLocation> {
  const res = await fetch(storePath("plazavea", "/location"));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { location: PlazaVeaDeliveryLocation };
  return data.location;
}

export async function setPlazaVeaLocation(input: {
  latitude: number | null;
  longitude: number | null;
  street: string;
  number: string;
  neighborhood: string;
  postalCode: string;
  city?: string;
  state?: string;
  complement?: string;
  reference?: string;
  salesChannel?: string;
}): Promise<PlazaVeaDeliveryLocation> {
  const res = await fetch(storePath("plazavea", "/location"), postJson(input));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { location: PlazaVeaDeliveryLocation };
  return data.location;
}

export async function fetchWongLocation(): Promise<WongLocationState> {
  const res = await fetch(storePath("wong", "/location"));
  if (!res.ok) throw new Error(await readApiError(res));
  return await res.json() as WongLocationState;
}

export async function setWongLocation(addressId: string): Promise<WongLocationState> {
  const res = await fetch(storePath("wong", "/location"), postJson({ addressId }));
  if (!res.ok) throw new Error(await readApiError(res));
  return await res.json() as WongLocationState;
}

export async function fetchPlazaVeaCoverage(): Promise<PlazaVeaCoverage> {
  const res = await fetch(storePath("plazavea", "/coverage"));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { coverage: PlazaVeaCoverage };
  return data.coverage;
}

export async function reverseGeocodePlazaVeaLocation(latitude: number, longitude: number): Promise<PlazaVeaReverseGeocodeResult> {
  const params = new URLSearchParams({ lat: String(latitude), lon: String(longitude) });
  const res = await fetch(storePath("plazavea", `/reverse-geocode?${params}`));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { result: PlazaVeaReverseGeocodeResult };
  return data.result;
}

export async function searchStoreProducts(store: StoreId, query: string): Promise<StoreProduct[]> {
  const params = new URLSearchParams({ q: query });
  const res = await fetch(storePath(store, `/search?${params}`));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { products: StoreProduct[] };
  return data.products;
}

export async function fetchStoreMappings(store: StoreId): Promise<StoreMapping[]> {
  const res = await fetch(storePath(store, "/mappings"));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { mappings: StoreMapping[] };
  return data.mappings;
}

export async function saveStoreMapping(store: StoreId, input: {
  ingredientName: string;
  skuId: string;
  sellerId?: string;
  conversionMode: StoreMapping["conversionMode"];
  packageAmount?: number | null;
  packageUnit?: string | null;
  unitsPerSku?: number | null;
}): Promise<StoreMapping> {
  const res = await fetch(storePath(store, "/mappings"), postJson(input));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { mapping: StoreMapping };
  return data.mapping;
}

export async function deleteStoreMapping(store: StoreId, ingredientName: string): Promise<void> {
  const params = new URLSearchParams({ ingredientName });
  const res = await fetch(storePath(store, `/mappings?${params}`), { method: "DELETE" });
  if (!res.ok) throw new Error(await readApiError(res));
}

export async function addStoreMappedCartItems(
  store: StoreId,
  weekId: string,
  items: StoreCartAddItem[],
): Promise<StoreCartAddResult> {
  const res = await fetch(storePath(store, "/cart/add"), postJson({ weekId, items }));
  if (!res.ok) throw new Error(await readApiError(res));
  return await res.json() as StoreCartAddResult;
}

export async function fetchStoreCart(store: StoreId, weekId: string): Promise<StoreCart | null> {
  const res = await fetch(storePath(store, `/cart?weekId=${encodeURIComponent(weekId)}`));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { cart: StoreCart | null };
  return data.cart;
}

export async function removeStoreCartItem(
  store: StoreId,
  weekId: string,
  skuId: string,
  sellerId?: string,
): Promise<StoreCart> {
  const res = await fetch(storePath(store, "/cart/remove"), postJson({ weekId, skuId, sellerId }));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { cart: StoreCart };
  return data.cart;
}

export async function setStoreCartItemQuantity(
  store: StoreId,
  weekId: string,
  ingredientName: string,
  totalGrams: number,
  totalUnits: number,
): Promise<{ cart: StoreCart; quantity: number }> {
  const res = await fetch(
    storePath(store, "/cart/set-quantity"),
    postJson({ weekId, ingredientName, totalGrams, totalUnits }),
  );
  if (!res.ok) throw new Error(await readApiError(res));
  return await res.json() as { cart: StoreCart; quantity: number };
}

export async function setStoreCartSkuQuantity(
  store: StoreId,
  weekId: string,
  skuId: string,
  quantity: number,
  sellerId?: string,
): Promise<StoreCart | null> {
  const res = await fetch(storePath(store, "/cart/set-sku-quantity"), postJson({ weekId, skuId, sellerId, quantity }));
  if (!res.ok) throw new Error(await readApiError(res));
  const data = await res.json() as { cart: StoreCart | null };
  return data.cart;
}
